import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import MentionTextarea from '../components/MentionTextarea';
import SignalText from '../components/SignalText';
import { SurfacePage, SoftSurface } from '../components/surface';
import {
  ClubPanel, ClubBackPill, PillButton, TagPill, useArmed, useToast, Toast,
  MUTED, SUBTLE, PINNED, RULE, RED, CYAN, MAGENTA, panelTitle, metaRight,
} from '../components/club/clubUi';
import clubService from '../services/clubService';
import { useDialog } from '../context/DialogContext';
import { useUser } from '../context/UserContext';
import useIsMobile from '../hooks/useIsMobile';
import { safeBottom } from '../utils/safeArea';
import { ago, dateLabel, plural } from '../utils/surfaceFormat';
import { tintFor } from '../utils/tints';

/**
 * /clubs/:id/forum/:threadId — design_handoff_club_pages/README.md §4 "Forum thread". 900px:
 * the thread card (PINNED, category, `N REPLIES · LAST 2H AGO`, admin PIN / DELETE armed, title,
 * author with a cyan HOST pill, body with magenta @mentions), the replies card (rows alternating
 * white / #FAFAFA, REPLY + armed DELETE), the composer under a 2px black rule. Phone: a sticky
 * white pill composer above the bottom nav. docs/plans/2026-08-club-forum/PLAN.md
 */

/** `LAST 40M AGO` / `LAST YESTERDAY` / `LAST MAR 4` — the handoff meta line. */
function lastLabel(iso) {
  const a = ago(iso);
  if (!a) return null;
  if (/^\d+[MH]$/.test(a)) return `LAST ${a} AGO`;
  return `LAST ${a}`;
}

const isHostRole = (role) => role === 'owner' || role === 'admin';
const TEXTAREA_SOFT = '!border-0 !bg-[#F5F5F5] rounded-[18px]';

function Face({ src, name, tintKey, size = 40 }) {
  return (
    <span className="flex flex-none items-center justify-center overflow-hidden rounded-full" style={{ width: size, height: size, background: src ? '#E5E7EB' : tintFor(tintKey || name), fontSize: Math.round(size * 0.36), fontWeight: 800 }}>
      {src ? <img src={src} alt="" referrerPolicy="no-referrer" className="block h-full w-full object-cover" /> : (name || '?').charAt(0).toUpperCase()}
    </span>
  );
}

/** Body copy with @mentions in magenta bold (the MentionText markup, recoloured). */
function Body({ text, size }) {
  return (
    <span className="club-thread-body whitespace-pre-wrap" style={{ fontSize: size, lineHeight: 1.7, overflowWrap: 'anywhere' }}>
      <style>{'.club-thread-body .cursor-pointer { color: #FF00AA !important; font-weight: 700; }'}</style>
      <SignalText text={text} />
    </span>
  );
}

export default function ClubForumThreadPage() {
  const { id: clubId, threadId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useDialog();
  const { profile } = useUser();
  const phone = useIsMobile();
  const composerRef = useRef(null);
  const { toast, say } = useToast();
  const { isArmed, confirm, disarm } = useArmed();

  const [detail, setDetail] = useState(null);
  const [club, setClub] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reply, setReply] = useState('');
  const [tagged, setTagged] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await clubService.getForumThread(threadId);
    if (r?.success) { setDetail(r.data); setError(null); }
    else setError(r?.error?.message || r?.error || 'This thread is not available.');
    setLoading(false);
  }, [threadId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      const [r, c] = await Promise.all([clubService.listForumMembers(clubId), clubService.getClub(clubId)]);
      if (r?.success) setMembers(r.data || []);
      if (c?.success) setClub(c.data || null);
    })();
  }, [clubId]);

  const thread = detail?.thread || null;
  const replies = detail?.replies || [];

  const goForum = () => navigate(`/clubs/${clubId}/forum`);

  const focusComposer = () => {
    const el = composerRef.current?.querySelector('textarea, input');
    if (el) { el.focus(); el.scrollIntoView?.({ behavior: 'smooth', block: 'center' }); }
  };

  const send = async () => {
    if (!reply.trim() || busy) return;
    setBusy(true);
    const r = await clubService.addForumReply(threadId, {
      body: reply.trim(),
      mentionedUserIds: tagged.map((t) => t.userId),
    });
    setBusy(false);
    if (r?.success) { say(tagged.length ? `REPLIED · ${tagged.length} TAGGED` : 'REPLIED'); setReply(''); setTagged([]); load(); }
    else showAlert({ title: 'COULD NOT REPLY', message: r?.error?.message || r?.error || 'Try again.', type: 'error' });
  };

  const removeReply = async (id) => {
    if (!confirm(`reply:${id}`)) return;
    const r = await clubService.deleteForumReply(id);
    if (r && r.success === false) { await showAlert({ title: 'COULD NOT DELETE', message: r.error?.message || r.error || 'Try again.', type: 'error' }); return; }
    say('REPLY DELETED');
    load();
  };

  const togglePin = async () => {
    if (!thread) return;
    const r = await clubService.setForumThreadPinned(threadId, !thread.isPinned);
    if (r && r.success === false) { await showAlert({ title: 'COULD NOT PIN', message: r.error?.message || r.error || 'Try again.', type: 'error' }); return; }
    load();
  };

  const removeThread = async () => {
    if (!confirm('thread')) return;
    const r = await clubService.deleteForumThread(threadId);
    if (r && r.success === false) { await showAlert({ title: 'COULD NOT DELETE', message: r.error?.message || r.error || 'Try again.', type: 'error' }); return; }
    goForum();
  };

  // REPLY on a row: tag the author (by handle when they have one, by id regardless) and
  // drop the caret in the composer.
  const replyTo = (r) => {
    const member = members.find((m) => m.userId === r.authorUserId);
    const handle = member?.handle;
    if (handle) setReply((prev) => (prev.includes(`@${handle}`) ? prev : `@${handle} ${prev}`));
    if (r.authorUserId) setTagged((prev) => (prev.some((t) => t.userId === r.authorUserId) ? prev : [...prev, { userId: r.authorUserId, token: handle ? `@${handle}` : null, displayName: r.authorName }]));
    focusComposer();
  };

  const onTag = (t) => setTagged((prev) => (prev.some((x) => x.userId === t.userId) ? prev : [...prev, t]));

  const category = thread?.categoryName ? thread.categoryName.toUpperCase() : null;
  const clubName = (club?.name || '').toUpperCase();
  const canSend = !!reply.trim() && !busy;
  const tagLine = tagged.length ? `TAGGING ${tagged.map((t) => ((t.displayName || t.token || '').replace(/^@/, '').split(' ')[0] || '').toUpperCase()).filter(Boolean).join(', ')}` : '';

  const frame = (children, { bar = null } = {}) => (
    <SoftSurface>
      <SurfacePage bare showTopBarOnMobile>
        <main className="mx-auto flex w-full flex-col font-mono" style={{ maxWidth: 900, padding: phone ? '12px 12px 24px' : '24px 32px 72px', gap: phone ? 10 : 16, boxSizing: 'border-box' }} data-testid="club-thread">
          <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
            <ClubBackPill onClick={goForum}>FORUM</ClubBackPill>
            {(clubName || category) && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: MUTED }} data-testid="thread-eyebrow">{[clubName, category].filter(Boolean).join(' · ')}</span>}
          </div>
          {children}
        </main>
        {bar}
        <Toast text={toast} phone={phone} bottom={phone ? 150 : 32} />
      </SurfacePage>
    </SoftSurface>
  );

  if (loading && !detail) return frame(<div className="flex justify-center py-20"><LoadingSpinner /></div>);
  if (error || !thread) {
    return frame(
      <section className="flex flex-col items-center bg-white text-center" style={{ borderRadius: 24, padding: '40px 24px', gap: 12 }} data-testid="thread-unavailable">
        <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: '.16em' }}>THIS THREAD ISN’T OPEN TO YOU.</span>
        <span style={{ maxWidth: 380, fontSize: 12, lineHeight: 1.6, color: MUTED }}>{error || 'It may have been deleted, or it belongs to a club you aren’t in.'}</span>
        <PillButton size={42} fontSize={10} onClick={goForum} style={{ padding: '0 20px' }}>← BACK TO THE FORUM</PillButton>
      </section>,
    );
  }

  const replyCount = thread.replyCount ?? replies.length;
  const metaLine = [plural(replyCount, 'REPLY', 'REPLIES'), lastLabel(thread.lastActivityAt || thread.createdAt)].filter(Boolean).join(' · ');
  const threadArmed = isArmed('thread');

  const modButtons = (thread.canPin || thread.canModerate) && (
    <span className="flex" style={{ marginLeft: 'auto', gap: 6 }} data-testid="thread-mod">
      {thread.canPin && <PillButton variant="tertiary" size={32} fontSize={9} onClick={togglePin} style={{ padding: '0 14px' }}>{thread.isPinned ? 'UNPIN' : 'PIN'}</PillButton>}
      {thread.canModerate && (
        <PillButton variant={threadArmed ? 'armed' : 'danger'} size={32} fontSize={9} onClick={removeThread} onBlur={threadArmed ? disarm : undefined} style={{ padding: '0 14px' }} data-testid="delete-thread">
          {threadArmed ? 'DELETE THREAD + REPLIES?' : 'DELETE'}
        </PillButton>
      )}
    </span>
  );

  const threadCard = (
    <ClubPanel phone={phone} style={{ padding: phone ? '18px 16px' : '26px 28px' }} data-testid="thread-header">
      <span className="flex flex-wrap items-center" style={{ gap: 8 }}>
        {thread.isPinned && <TagPill bg={PINNED} ring="#000" size={9} data-testid="pinned-tag">PINNED</TagPill>}
        {category && <TagPill size={9}>{category}</TagPill>}
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.12em', color: MUTED }} data-testid="thread-meta">{metaLine}</span>
        {modButtons}
      </span>
      <h1 className="m-0" style={{ fontSize: phone ? 22 : 30, fontWeight: 700, lineHeight: 1.15, letterSpacing: '-.01em', textTransform: 'none', textWrap: 'pretty', overflowWrap: 'anywhere' }}>{thread.title}</h1>
      <span className="flex items-center" style={{ gap: 12 }}>
        <Face src={thread.authorAvatarUrl} name={thread.authorName} tintKey={thread.authorUserId} size={44} />
        <span className="flex flex-col" style={{ gap: 3 }}>
          <span className="flex items-center" style={{ gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{thread.authorName}</span>
            {isHostRole(thread.authorRole) && <TagPill bg={CYAN} data-testid="host-badge-thread">HOST</TagPill>}
          </span>
          <span style={{ fontSize: 11, color: MUTED }}>{[thread.authorHandle ? `@${thread.authorHandle}` : null, dateLabel(thread.createdAt)].filter(Boolean).join(' · ')}</span>
        </span>
      </span>
      {thread.body && <p className="m-0"><Body text={thread.body} size={15} /></p>}
    </ClubPanel>
  );

  const rowPad = phone ? '14px 16px' : '16px 24px';
  const replyRows = replies.map((r, i) => {
    const armed = isArmed(`reply:${r.id}`);
    return (
      <div key={r.id} className="flex" style={{ gap: phone ? 10 : 14, padding: rowPad, background: i % 2 ? '#FAFAFA' : '#fff', borderTop: RULE }} data-testid={`reply-${r.id}`}>
        <Face src={r.authorAvatarUrl} name={r.authorName} tintKey={r.authorUserId} size={phone ? 34 : 40} />
        <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 6 }}>
          <span className="flex flex-wrap items-center" style={{ gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{r.authorName}</span>
            <span style={{ fontSize: 11, color: SUBTLE }}>· {dateLabel(r.createdAt)}</span>
            {isHostRole(r.authorRole) && <TagPill bg={CYAN} data-testid={`host-badge-${r.id}`}>HOST</TagPill>}
          </span>
          <Body text={r.body} size={14} />
          <span className="flex" style={{ gap: 16, paddingTop: 2 }}>
            <button type="button" onClick={() => replyTo(r)} className="font-mono uppercase" style={{ border: 'none', background: 'none', padding: 0, fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: MUTED, cursor: 'pointer' }} data-testid={`reply-to-${r.id}`}>REPLY</button>
            {(r.canModerate || r.isMine) && (
              <button type="button" onClick={() => removeReply(r.id)} onBlur={armed ? disarm : undefined} className="font-mono uppercase" style={{ border: 'none', background: armed ? RED : 'none', color: armed ? '#fff' : RED, padding: armed ? '2px 8px' : 0, borderRadius: 999, fontSize: 9, fontWeight: 700, letterSpacing: '.14em', cursor: 'pointer' }} data-testid={`delete-reply-${r.id}`}>
                {armed ? 'CONFIRM DELETE' : 'DELETE'}
              </button>
            )}
          </span>
        </span>
      </div>
    );
  });

  const viewerFace = (size) => <Face src={profile?.avatarUrl || profile?.faceUrl} name={profile?.displayName} tintKey={profile?.userId} size={size} />;

  const repliesCard = (
    <section className="flex flex-col overflow-hidden bg-white font-mono" style={{ borderRadius: phone ? 22 : 24 }} data-testid="thread-replies">
      <span className="flex justify-between" style={{ gap: 10, padding: rowPad }}>
        <span style={panelTitle}>{plural(replies.length, 'REPLY', 'REPLIES')}</span>
        <span style={metaRight}>OLDEST FIRST</span>
      </span>
      {replies.length === 0 && <span style={{ padding: rowPad, borderTop: RULE, fontSize: 13, color: MUTED }} data-testid="no-replies">No replies yet. Start it off.</span>}
      {replyRows}
      {!phone && (
        <div ref={composerRef} className="flex" style={{ gap: 14, padding: '18px 24px 22px', borderTop: '2px solid #000' }} data-testid="composer">
          {viewerFace(40)}
          <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 10 }}>
            <MentionTextarea value={reply} onChange={setReply} people={members} onTag={onTag} rows={3} placeholder="Write a reply… type @ to tag someone" className={TEXTAREA_SOFT} />
            <span className="flex items-center" style={{ gap: 10 }}>
              <span style={{ fontSize: 11, color: MUTED }}>Members of this club only</span>
              {tagLine && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.12em', color: MAGENTA }} data-testid="tag-line">{tagLine}</span>}
              <PillButton size={44} fontSize={11} onClick={send} disabled={!canSend} style={{ marginLeft: 'auto', padding: '0 22px', opacity: reply.trim() ? 1 : 0.35 }} data-testid="post-reply">{busy ? 'POSTING…' : 'POST REPLY'}</PillButton>
            </span>
          </span>
        </div>
      )}
    </section>
  );

  const phoneBar = phone && (
    <div className="sticky" style={{ bottom: safeBottom(78), zIndex: 39, padding: '0 12px 8px' }} data-testid="composer-bar">
      <div ref={composerRef} className="flex items-center bg-white font-mono" style={{ gap: 8, padding: 6, borderRadius: 999, boxShadow: 'inset 0 0 0 2px #000' }} data-testid="composer">
        {viewerFace(36)}
        <div className="min-w-0 flex-1">
          <MentionTextarea value={reply} onChange={setReply} people={members} onTag={onTag} singleLine placeholder="Write a reply…" className="!border-0 !bg-transparent" />
        </div>
        <PillButton size={44} fontSize={11} onClick={send} disabled={!canSend} style={{ padding: '0 18px', opacity: reply.trim() ? 1 : 0.35 }}>{busy ? '…' : 'POST'}</PillButton>
      </div>
    </div>
  );

  return frame(
    <>
      {threadCard}
      {repliesCard}
      {phone && <div style={{ height: 8 }} />}
    </>,
    { bar: phoneBar },
  );
}

